import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { message } from 'antd';
import { getWorkWiseAuthToken, isEffectivelyOffline, decodeWorkWiseToken } from '../../utils/helper';
import { useOfflineToggle } from '../OfflineToggle/useOfflineToggle';
import {
  getLocalPresentations,
  renameLocalPresentation,
  removeLocalPresentation,
  createLocalPresentation,
  isLocalPresentationLimitReached,
  MAX_LOCAL_PRESENTATIONS,
} from '../../utils/localPresentations';
import { workwiseServices } from '../WorkW-Api/workWiseApi';
import { templateService, adminService } from '../../store/api';
import { useSelector, useDispatch } from 'react-redux';
import { slideAction } from '../../store/slideSlice';
import Sidebar from './components/Sidebar/Sidebar';
import Header from './components/Header/Header';
import CreateSlideDrawer from './components/CreateSlideDrawer/CreateSlideDrawer';
import CreateFolderDrawer from './components/CreateSlideDrawer/CreateFolderDrawer';
import CreateTemplateDrawer from './components/CreateTemplateDrawer/CreateTemplateDrawer';
import ApplicationsDrawer from './components/ApplicationsDrawer/ApplicationsDrawer';
import SyncPresentationModal from '../SyncPresentationModal/SyncPresentationModal';
import DocumentCard from './components/DocumentCard/DocumentCard';
import DocumentList from './components/DocumentList/DocumentListItem';
import DashboardSkeleton from './components/DashboardSkeleton';
import UnauthorizedView from './components/UnauthorizedView';
import Spinner from '../Spinner';
import './Dashboard.css';
import TemplateGallery from './components/TemplateGallery';

// shared list of template categories; exported for reuse
export const TEMPLATE_CATEGORIES = [
  'Business',
  'Education',
  'Technology',
  'Marketing',
  'HR',
  'Finance',
  'Healthcare',
  'Design',
  'Legal',
  'Other',
];

const Dashboard = () => {
  const isAuthenticated = useSelector(state => state.slide.isAuthenticated);
  const dispatch = useDispatch();
  const { offline } = useOfflineToggle();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeSidebarItem, setActiveSidebarItem] = useState('all-documents');
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Tracks whether the very first load finished. First load shows the full
  // skeleton; every reload after shows the lightweight content spinner.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  // Sorting: each option provides sortBy (field) and sort (direction) numeric codes required by API
  const SORT_OPTIONS = [
    { id: 1, label: 'Create Date-Desc', sortBy: 1, sort: 2 },
    { id: 2, label: 'Create Date-Asc', sortBy: 2, sort: 1 },
    { id: 3, label: 'Name-Desc', sortBy: 3, sort: 2 },
    { id: 4, label: 'Name-Asc', sortBy: 4, sort: 1 },
    { id: 5, label: 'Update Date-Desc', sortBy: 5, sort: 2 },
    { id: 6, label: 'Update Date-Asc', sortBy: 6, sort: 1 },
  ];
  const [selectedSort, setSelectedSort] = useState(SORT_OPTIONS[0]); // Default to Update Date-Desc (most recent first)
  const [sortOpen, setSortOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [myDrive, setMyDrive] = useState(false);
  const [appsDrawerOpen, setAppsDrawerOpen] = useState(false);
  const [folderDrawerOpen, setFolderDrawerOpen] = useState(false);
  const [folders, setFolders] = useState([]);
  const [parentId, setParentId] = useState('00000000-0000-0000-0000-000000000000');
  const [types, setTypes] = useState([7]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [adminsLoading, setAdminsLoading] = useState(true);
  const [templateDrawerOpen, setTemplateDrawerOpen] = useState(false);
  const [localDocs, setLocalDocs] = useState([]);
  const [syncDoc, setSyncDoc] = useState(null); // local presentation pending sync, or null

  const isFirstRender = useRef(true);

  // Local (offline-created) presentations live in localStorage.
  const loadLocalDocs = () => setLocalDocs(getLocalPresentations());

  const handleLocalRename = (id, name) => {
    renameLocalPresentation(id, name);
    loadLocalDocs();
  };

  const handleLocalDelete = (id) => {
    removeLocalPresentation(id);
    loadLocalDocs();
  };

  // Syncing creates a real server document, so it needs connectivity.
  const handleLocalSync = (localDoc) => {
    if (isEffectivelyOffline()) {
      message.warning('You are offline. Connect to the internet to sync this presentation.');
      return;
    }
    setSyncDoc(localDoc);
  };

  const handleLocalSynced = () => {
    loadLocalDocs();
    // Clear any active search/page first: a filtered list would hide the very
    // document we just uploaded and look like the sync did nothing.
    setSearch('');
    resetPagination();
    loadDocs(1, pageSize, viewMode === 'grid'); // reflect the new server document
  };

  // "Create Document". Offline we skip the drawer entirely and create a local
  // presentation directly — there are no server fields to fill in offline.
  const handleNewDocument = () => {
    if (!isEffectivelyOffline()) {
      setCreateOpen(true);
      return;
    }

    if (isLocalPresentationLimitReached()) {
      message.warning(
        `You already have ${MAX_LOCAL_PRESENTATIONS} local presentations. Delete one to create a new local presentation.`
      );
      return;
    }

    const user = decodeWorkWiseToken();
    const creator = user
      ? { id: user.userId || user.id, name: user.name, image: user.image }
      : null;

    const record = createLocalPresentation({ name: 'Untitled presentation', creator });
    if (!record) {
      message.warning(
        `You already have ${MAX_LOCAL_PRESENTATIONS} local presentations. Delete one to create a new local presentation.`
      );
      return;
    }

    loadLocalDocs();
    message.success('Local presentation created');
    window.open(`/mileslides/${record.id}?local=true`, '_blank');
  };

  // Handler for Shared with me
  const handleSharedWithMe = () => {
    setMyDrive(true);
    setActiveSidebarItem('my-drive');
    resetPagination();
  };

  const handleActiveItemChange = (item) => {
    setActiveSidebarItem(item);
    if (item === 'all-documents') {
      setMyDrive(false);
      setParentId('00000000-0000-0000-0000-000000000000');
      setTypes([7]);
      resetPagination();
    } else if (item === 'my-drive') {
      setMyDrive(true);
      setParentId('00000000-0000-0000-0000-000000000000');
      setTypes([7]);
      resetPagination();
    } else {
      // Assume it's a folder id
      setMyDrive(false);
      setParentId(item);
      setTypes([]);
      resetPagination();
    }
  };

  // Load documents from API
  const loadDocs = async (page = 1, size = 10, loadAll = false) => {
    // Offline: there are no server documents to fetch. Skip the request so we
    // don't error out or trip the auth gate — local presentations show regardless.
    if (isEffectivelyOffline()) {
      setDocs([]);
      setError('');
      setLoading(false);
      setHasLoadedOnce(true);
      return;
    }
    setLoading(true);
    setError('');
    // Only set activeSidebarItem if not viewing a specific folder
    if (parentId === '00000000-0000-0000-0000-000000000000') {
      setActiveSidebarItem(myDrive ? 'my-drive' : 'all-documents');
    }
    try {
      // Initialize sidebar state from cookie
      try {
        const cookieMatch = document.cookie.match(/(?:^|; )sidebar_state=([^;]+)/);
        if (cookieMatch) {
          const val = cookieMatch[1] === 'true';
          setSidebarOpen(val);
        }
      } catch { }

      const payload = {
        pageNo: loadAll ? 1 : page,
        pageSize: loadAll ? 50 : size, // Load many documents for grid view
        search, // 🔥 use search param
        referenceId: '00000000-0000-0000-0000-000000000000',
        parentId,
        myDrive: myDrive,
        referenceType: 1,
        sortBy: selectedSort.sortBy,
        onlyFiles: false,
        types,
      };
      const res = await workwiseServices.getAllDocumentList(payload);
      setDocs(Array.isArray(res?.data?.data) ? res.data.data : []);
      // Set authentication to true on successful API call
      dispatch(slideAction.setIsAuthenticated(true));
    } catch (e) {
      if (e.response?.status === 401) {
        dispatch(slideAction.setIsAuthenticated(false));
      } else {
        setError(e?.message || 'Failed to load documents');
      }
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  };

  const handleRename = async (payload) => {
    try {
      await workwiseServices.updateDocumentName(payload);
      message.success('Document renamed successfully');
      await loadDocs(currentPage, pageSize, viewMode === 'grid'); // Reload to reflect changes
    } catch (e) {
      console.error('Failed to rename document:', e);
      const errorMessage = e.response?.data?.message || e.message || 'Failed to rename document. Please try again.';
      message.error(errorMessage);
      throw e; // Re-throw so DocumentCard can handle it
    }
  };

  const handlePaginationChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
    loadDocs(page, size);
  };

  // Reset pagination when filters change
  const resetPagination = () => {
    setCurrentPage(1);
  };

  // Load folders from API
  const loadFolders = async () => {
    if (isEffectivelyOffline()) {
      setFolders([]);
      return;
    }
    try {
      const payload = {
        pageNo: 1,
        pageSize: 50,
        search: '',
        referenceId: '00000000-0000-0000-0000-000000000000',
        parentId: '00000000-0000-0000-0000-000000000000',
        myDrive: false,
        referenceType: 1,
        sortBy: 1,
        onlyFiles: false,
        types: [1],
      };
      const res = await workwiseServices.getAllDocumentList(payload);
      setFolders(Array.isArray(res?.data?.data) ? res.data.data : []);
      // Set authentication to true on successful API call
      dispatch(slideAction.setIsAuthenticated(true));
    } catch (e) {
      if (e.response?.status === 401) {
        dispatch(slideAction.setIsAuthenticated(false));
      } else {
        console.error('Failed to load folders:', e);
      }
    }
  };

  // Load templates from API
  const loadTemplates = async () => {
    if (isEffectivelyOffline()) {
      setTemplates([]);
      setTemplatesLoading(false);
      return;
    }
    setTemplatesLoading(true);
    try {
      const res = await templateService.getAllTemplates();
      setTemplates(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (e) {
      console.error('Failed to load templates:', e);
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Load admins from API and save to global state
  const loadAdmins = async () => {
    if (isEffectivelyOffline()) {
      dispatch(slideAction.setAdmins([]));
      setAdminsLoading(false);
      return;
    }
    setAdminsLoading(true);
    try {
      const adminRes = await adminService.getAllAdmins();
      const adminsData = Array.isArray(adminRes?.data?.data) ? adminRes.data.data : [];
      dispatch(slideAction.setAdmins(adminsData));
      return adminsData;
    } catch (adminErr) {
      console.error('Failed to load admin templates:', adminErr);
      dispatch(slideAction.setAdmins([]));
      return [];
    } finally {
      setAdminsLoading(false);
    }
  };

  // Load documents on search change (debounced)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    let isMounted = true;

    async function load() {
      resetPagination();
      await loadDocs(1, pageSize, viewMode === 'grid'); // Load all for grid, paginated for list
    }

    // debounce to avoid firing API on every keystroke instantly
    const timeout = setTimeout(load, 400);

    return () => {
      isMounted = false;
      clearTimeout(timeout);
    };
  }, [search]);

  // Load documents on other filter changes (immediate)
  useEffect(() => {
    resetPagination();
    loadDocs(1, pageSize, viewMode === 'grid');
  }, [selectedSort, myDrive, parentId, types, viewMode]);

  // Load folders on mount
  useEffect(() => {
    loadFolders();
  }, []);

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, []);

  // Load admins when the user is authenticated; otherwise resolve the admins
  // loading flag so the dashboard can settle (and show the unauthorized screen)
  // for signed-out users instead of hanging on the skeleton forever.
  useEffect(() => {
    if (isAuthenticated) {
      loadAdmins();
    } else {
      setAdminsLoading(false);
    }
  }, [isAuthenticated]);

  // Check authentication on mount
  useEffect(() => {
    if (getWorkWiseAuthToken()) {
      dispatch(slideAction.setIsAuthenticated(true));
    }
  }, []);

  // Local presentations: load on mount and refresh when one is created/edited in
  // another tab (storage event) or when the user returns to this tab (focus).
  useEffect(() => {
    loadLocalDocs();
    const onFocus = () => loadLocalDocs();
    const onStorage = (e) => {
      if (!e || e.key === null || e.key === 'local_presentation_slide') loadLocalDocs();
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // When connectivity returns (real recovery or the manual toggle), refetch all
  // server data instead of leaving the user on the local-only view — and instead
  // of flashing the unauthorized view while isAuthenticated is still false.
  // useLayoutEffect + an immediate loading flag keep the loader on screen from
  // the very next paint.
  const wasOfflineRef = useRef(offline);
  useLayoutEffect(() => {
    const cameOnline = wasOfflineRef.current && !offline;
    wasOfflineRef.current = offline;
    if (!cameOnline) return;

    setLoading(true);
    resetPagination();
    loadDocs(1, pageSize, viewMode === 'grid');
    loadFolders();
    loadTemplates();
    loadAdmins();
  }, [offline]);

  // Persist sidebar state in cookie
  useEffect(() => {
    try {
      document.cookie = `sidebar_state=${sidebarOpen}; path=/; max-age=${60 * 60 * 24 * 7
        }`;
    } catch { }
  }, [sidebarOpen]);

  // Keyboard shortcut Ctrl/Cmd + B
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'b' || e.key === 'B')) {
        e.preventDefault();
        setSidebarOpen((s) => !s);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function openLoginWindow() {
    const width = 900; // Desired width in pixels
    const height = 600; // Desired height in pixels
    const url = "https://workw.com/login"; // Or any other URL you want to load
    const windowName = "Workwise Authentication";
    const left = (window.screen.width / 2) - (width / 2);
    const top = 50;
    // Define window features
    const features = `width=${width},height=${height},left=${left},top=${top},
                    toolbar=no,menubar=no,scrollbars=no,resizable=yes,
                    location=no,directories=no,status=no`;
    // const features = `width=${width},height=${height},resizable=yes,scrollbars=yes`;

    window.open(url, windowName, features);
  }

  const isDashboardLoading = loading || templatesLoading || adminsLoading;
  // Full skeleton only on the initial load; spinner for every reload after.
  const showSkeleton = isDashboardLoading && !hasLoadedOnce;
  const isReloading = loading && hasLoadedOnce;

  // While the dashboard is still loading, show the full-page skeleton. This also
  // covers the brief window before auth resolves, so signed-in users never flash
  // the unauthorized screen (matches milepad).
  if (showSkeleton) {
    return (
      <div className="dashboard-container">
        <DashboardSkeleton />
      </div>
    );
  }

  // Loading finished and the user is not signed in → full-page "Access required"
  // screen, with no dashboard chrome (sidebar/header). Offline bypasses the gate
  // entirely so local presentations stay reachable without a server session.
  if (!offline && !isAuthenticated) {
    return (
      <div className="dashboard-container">
        <UnauthorizedView onLogin={openLoginWindow} />
      </div>
    );
  }

  // Presentations only — the endpoint also returns other document types.
  const recentDocs = docs.filter((doc) => doc.documentType === 7);

  return (
    <div className="dashboard-container">
      <div className="dashboard-flex">
        <AnimatePresence initial={false}>
          <motion.div
            key={sidebarOpen ? 'open' : 'closed'}
            initial={{ width: sidebarOpen ? '22rem' : '4rem' }}
            animate={{ width: sidebarOpen ? '22rem' : '4rem' }}
            exit={{ width: '4rem' }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="relative"
          >
            <Sidebar
              open={sidebarOpen}
              onToggle={() => setSidebarOpen((s) => !s)}
              search={search}
              onSearch={setSearch}
              onNewDocument={handleNewDocument}
              onSharedWithMe={handleSharedWithMe}
              activeItem={activeSidebarItem}
              onActiveItemChange={handleActiveItemChange}
              onNewFolder={() => setFolderDrawerOpen(true)}
              folders={folders}
            />
          </motion.div>
        </AnimatePresence>
        {sidebarOpen && (
          <div
            onClick={() => setSidebarOpen(false)}
            className="sidebar-overlay"
            style={{ display: 'block' }}
          />
        )}
        <main className="dashboard-main">
          <Header
            viewMode={viewMode}
            onToggleSidebar={() => {
              console.log('Hamburger clicked, opening drawer');
              setAppsDrawerOpen(true);
            }}
            onChangeView={setViewMode}
            sortDropdownOpen={sortOpen}
            onToggleSort={() => setSortOpen(o => !o)}
            sortOptions={SORT_OPTIONS}
            selectedSort={selectedSort}
            onSelectSort={(opt) => { setSelectedSort(opt); setSortOpen(false); }}
          />

          <CreateSlideDrawer
            open={createOpen}
            onClose={() => { setCreateOpen(false); setSelectedTemplate(null); }}
            onCreate={() => loadDocs(currentPage, pageSize, viewMode === 'grid')}
            template={selectedTemplate}
          />

          <CreateFolderDrawer
            open={folderDrawerOpen}
            onClose={() => setFolderDrawerOpen(false)}
            onCreate={() => {
              loadDocs(currentPage, pageSize, viewMode === 'grid');
              loadFolders();
            }}
          />

          <ApplicationsDrawer
            open={appsDrawerOpen}
            onClose={() => setAppsDrawerOpen(false)}
          />

          <SyncPresentationModal
            open={!!syncDoc}
            doc={syncDoc}
            onClose={() => setSyncDoc(null)}
            onSynced={handleLocalSynced}
          />

          <div className="main-content">
            {isReloading ? (
              <Spinner label="Loading presentations…" />
            ) : error ? (
                <div className="error-container">
                  <div className="error-content">
                    {error}
                  </div>
                </div>
              ) : (
                <>
                  {/* Templates Gallery — hidden offline: templates are server-backed
                      and offline presentations always start blank. */}
                  {!offline && (
                    <>
                      <TemplateGallery
                        templates={templates}
                        onCreateBlank={() => setCreateOpen(true)}
                        onOpenTemplate={(template) => { setSelectedTemplate(template); setCreateOpen(true); }}
                        onCreateTemplate={() => setTemplateDrawerOpen(true)}
                        onTemplateDeleted={loadTemplates}
                      />
                      <CreateTemplateDrawer
                        open={templateDrawerOpen}
                        onClose={() => setTemplateDrawerOpen(false)}
                        onCreate={() => loadTemplates()}
                        categories={TEMPLATE_CATEGORIES}
                      />
                    </>
                  )}

                  {/* Local (offline-created) presentations */}
                  {localDocs.length > 0 && (
                    <>
                      <h2 className="dashboard-section-title">Local presentations</h2>
                      <div className="documents-grid">
                        {localDocs.map((doc, i) => (
                          <motion.div
                            key={doc.id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.03 }}
                          >
                            <motion.div
                              whileHover={{ y: -4 }}
                              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                            >
                              <DocumentCard
                                doc={doc}
                                isLocal
                                onLocalRename={handleLocalRename}
                                onDelete={handleLocalDelete}
                                onSync={handleLocalSync}
                              />
                            </motion.div>
                          </motion.div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Recent Documents */}
                  <h2 className="text-[1.12rem] font-semibold text-gray-900 mb-4">Recent documents</h2>
                  {/* Skipped entirely when empty: the list view is an antd Table
                      that draws its own "No Data" placeholder, which would sit
                      above our empty state and contradict it. */}
                  {recentDocs.length > 0 && (viewMode === 'grid' ? (
                    <div className="documents-grid">
                      {recentDocs.map((doc, i) => (
                        <motion.div
                          key={doc.id}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.03 }}
                        >
                          <motion.div
                            whileHover={{ y: -4 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                          >
                            <DocumentCard doc={doc} offline={offline} onRename={handleRename} />
                          </motion.div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <div className="documents-list">
                      <DocumentList
                        docs={recentDocs}
                        offline={offline}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        onPaginationChange={handlePaginationChange}
                      />
                    </div>
                  ))}

                  {/* Empty state sits outside the grid/list so it centres in the
                      content area instead of landing in the first grid cell. */}
                  {recentDocs.length === 0 && (
                    <div className="dashboard-empty-state">
                      <div className={`dashboard-empty-icon ${offline ? 'dashboard-empty-icon-offline' : ''}`}>
                        {offline ? (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M1 1l22 22" />
                            <path d="M16.72 11.06A10.94 10.94 0 0119 12.55" />
                            <path d="M5 12.55a10.94 10.94 0 015.17-2.39" />
                            <path d="M10.71 5.05A16 16 0 0122.58 9" />
                            <path d="M1.42 9a15.91 15.91 0 014.7-2.88" />
                            <path d="M8.53 16.11a6 6 0 016.95 0" />
                            <line x1="12" y1="20" x2="12.01" y2="20" />
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                            <path d="M14 2v6h6" />
                            <line x1="9" y1="13" x2="15" y2="13" />
                            <line x1="9" y1="17" x2="13" y2="17" />
                          </svg>
                        )}
                      </div>
                      <p className="dashboard-empty-title">
                        {offline ? "You're offline" : 'No documents found'}
                      </p>
                      <p className="dashboard-empty-subtitle">
                        {offline
                          ? 'Connect to the internet to see your recent presentations.'
                          : 'Create a new presentation to get started.'}
                      </p>
                    </div>
                  )}
                </>
              )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;